import { useCallback, useEffect, useRef } from "react";

import { useReadinessStore } from "../stores/readiness";
import type { ReadinessActiveResponse } from "../lib/readiness-types";

export type UseReadinessOptions = {
  // Either provide a stable Clerk token directly, or pass a `getToken`
  // resolver. Mirrors the pattern established by useEstimate / useCashToClose.
  token?: string;
  getToken?: () => Promise<string | null>;
  // Pass `enabled: false` to defer the fetch (e.g., before Clerk hydrates).
  enabled?: boolean;
};

export type UseReadinessState = {
  data: ReadinessActiveResponse | null;
  error: Error | null;
  // True only when there is no cached data yet and a fetch is in flight.
  // Drives empty-state spinners — must NOT flip back to true on background
  // refreshes, otherwise the sidebar flickers on every navigation.
  isLoading: boolean;
  // True when a fetch is in flight while cached data is already present.
  // Background indicators (subtle, optional) can read this instead.
  isRefreshing: boolean;
  // Imperatively trigger a revalidation. Mutation handlers call this from
  // `onSuccess` / `onStaleRebase` so readiness only updates on real changes,
  // not passive navigation.
  refresh: () => Promise<void>;
};

// useReadiness is a thin selector over `useReadinessStore`. It's intentionally
// a subscription, not a per-component cache: navigating between Dashboard,
// Settings, and Calculators must reuse the same readiness data instead of
// refetching on every mount (SILL-255).
export function useReadiness(opts: UseReadinessOptions = {}): UseReadinessState {
  const { token, getToken, enabled = true } = opts;

  const data = useReadinessStore((s) => s.data);
  const error = useReadinessStore((s) => s.error);
  const isFetching = useReadinessStore((s) => s.isFetching);
  const hasLoaded = useReadinessStore((s) => s.hasLoaded);

  // Capture the latest token resolver in a ref so the load effect can run
  // exactly once per (enabled) mount without retriggering when the parent
  // re-renders with a new `getToken` identity. This is the core fix for the
  // dashboard's `void activeView` token resolver thrash. The ref is updated
  // inside an effect (not during render) per React 19's react-hooks/refs.
  const resolverRef = useRef<() => Promise<string | null | undefined>>(
    () => Promise.resolve(undefined),
  );
  useEffect(() => {
    resolverRef.current = async () => {
      if (typeof getToken === "function") {
        return (await getToken()) ?? undefined;
      }
      return token;
    };
  }, [getToken, token]);

  useEffect(() => {
    if (!enabled) return;
    void useReadinessStore.getState().ensureLoaded(() => resolverRef.current());
  }, [enabled]);

  const refresh = useCallback(async () => {
    await useReadinessStore.getState().refresh(() => resolverRef.current());
  }, []);

  return {
    data,
    error,
    isLoading: enabled && isFetching && !hasLoaded,
    isRefreshing: isFetching && hasLoaded,
    refresh,
  };
}
